import React, { Fragment, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { ENTER_KEY_VALUE } from 'site-modules/shared/constants/key-values';
import { CalculatorStepsWrapper } from 'site-modules/calculators/components/calculator-steps-wrapper/calculator-steps-wrapper';
import { CalculatorStepItem } from 'site-modules/calculators/components/calculator-step-item/calculator-step-item';
import { CalculatorZipCode } from 'site-modules/calculators/components/calculator-zip-code/calculator-zip-code';
import { GasGuzzlerNewFuelEfficientVehicleStep } from 'site-modules/calculators/components/gas-guzzler-new-fuel-efficient-vehicle-step/gas-guzzler-new-fuel-efficient-vehicle-step';
import { GasGuzzlerTradeInStep } from 'site-modules/calculators/components/gas-guzzler-trade-in-step/gas-guzzler-trade-in-step';
import { GasGuzzlerGasMileageStep } from 'site-modules/calculators/components/gas-guzzler-gas-mileage-step/gas-guzzler-gas-mileage-step';
import { GasGuzzlerResultsStep } from 'site-modules/calculators/components/gas-guzzler-results-step/gas-guzzler-results-step';
import { AboutCalculator } from 'site-modules/calculators/components/about-calculator/about-calculator';
import { getMPGByStyle } from 'site-modules/shared/utils/calculator/api';
import { DEFAULT_VEHICLE_SALES_PRICE } from 'site-modules/shared/constants/calculator/calculator';

export const DEFAULT_NEW_VEHICLE_MPG = 30;
export const DEFAULT_YOUR_VEHICLE_PRICE = 15000;
export const DEFAULT_YOUR_VEHICLE_MPG = 20;
export const DEFAULT_MILEAGE_PER_MONTH = 1000;
export const DEFAULT_FUEL_PER_GALLON = 4.09;

export const getResultData = ({ mileagePerMonth, mpg, fuelPerGallon, tradeInMpg, vehicleSalesPrice, tradeInPrice }) => {
  const savings = Math.floor(
    Math.abs((mileagePerMonth / mpg) * fuelPerGallon - (mileagePerMonth / tradeInMpg) * fuelPerGallon)
  );
  const breakEvenPoint = Math.floor((vehicleSalesPrice - tradeInPrice) / savings);

  return {
    savings,
    breakEvenPoint,
  };
};

const ABOUT_ITEMS = [
  `You would like to save money on gas so you're considering trading in your gas guzzler for a more fuel efficient
car. This calculator shows how long will it take before you pay off the balance of a vehicle purchase and really
begin saving money.`,
  `Based on the EPA's MPG data and assuming 45% highway driving and 55% city driving. MPG data for model years 2007
and prior have been adjusted downward to reflect the EPA's new testing methodology adopted for 2008 model year
vehicles.`,
  `If you have selected a vehicle and entered your ZIP Code, we pre-populate each variable field in the Financial
Calculators with our estimates. These amounts are based on Edmunds.com True Market Value® prices and standard
industry data, but the values that apply to your purchase or lease are likely to vary. When available, you
should use the information provided to you by your dealer or lender.`,
];

export function GasGuzzlerCalculator({ isTradeInVehicleSelectorOpen = false }) {
  const [paymentFields, setPaymentFields] = useState({
    vehicleSalesPrice: DEFAULT_VEHICLE_SALES_PRICE,
    mpg: DEFAULT_NEW_VEHICLE_MPG,
    tradeInPrice: DEFAULT_YOUR_VEHICLE_PRICE,
    tradeInMpg: DEFAULT_YOUR_VEHICLE_MPG,
    mileagePerMonth: DEFAULT_MILEAGE_PER_MONTH,
    fuelPerGallon: DEFAULT_FUEL_PER_GALLON,
  });
  const [newStyle, setNewStyle] = useState(null);
  const [tradeInStyle, setTradeInStyle] = useState(null);

  const { savings, breakEvenPoint } = getResultData(paymentFields);

  /**
   * Handles input value change.
   * @param {string} name
   * @param {string} value
   */
  const handleInputChange = useCallback(
    ({ target: { name, value } }) => {
      if (paymentFields[`${name}`] !== Number(value)) {
        setPaymentFields(state => ({ ...state, [name]: Number(value) }));
      }
    },
    [paymentFields]
  );

  /**
   * Handles enter key press.
   * @param {Object} e
   */
  const handleKeyPress = useCallback(
    e => {
      if (e.key === ENTER_KEY_VALUE) handleInputChange(e);
    },
    [handleInputChange]
  );

  const saveVehicleSalesPrice = useCallback(
    (name, value) => {
      handleInputChange({ target: { name: 'vehicleSalesPrice', value } });
    },
    [handleInputChange]
  );

  const saveTradeInPrice = useCallback(
    (name, value) => {
      handleInputChange({ target: { name: 'tradeInPrice', value } });
    },
    [handleInputChange]
  );

  const saveNewVehicle = useCallback(
    ({ salesPrice = DEFAULT_VEHICLE_SALES_PRICE, style, mpg = DEFAULT_NEW_VEHICLE_MPG }) => {
      setNewStyle(style);
      setPaymentFields(state => ({ ...state, vehicleSalesPrice: salesPrice, mpg: Number(mpg) }));
    },
    []
  );

  const removeNewVehicle = useCallback(() => {
    setNewStyle(null);
    setPaymentFields(state => ({
      ...state,
      vehicleSalesPrice: DEFAULT_VEHICLE_SALES_PRICE,
      mpg: DEFAULT_NEW_VEHICLE_MPG,
    }));
  }, []);

  const saveTradeInVehicle = useCallback(async vehicle => {
    const { style } = vehicle;
    const mpg = await getMPGByStyle(style);

    setTradeInStyle(style);
    setPaymentFields(state => ({ ...state, tradeInMpg: mpg ? Number(mpg) : DEFAULT_YOUR_VEHICLE_MPG }));
  }, []);

  const removeTradeInVehicle = useCallback(() => {
    setTradeInStyle(null);
    setPaymentFields(state => ({
      ...state,
      tradeInPrice: DEFAULT_YOUR_VEHICLE_PRICE,
      tradeInMpg: DEFAULT_YOUR_VEHICLE_MPG,
    }));
  }, []);

  return (
    <Fragment>
      <CalculatorStepsWrapper className="Calculate your fuel cost savings estimate">
        <CalculatorStepItem stepName="Location">
          <CalculatorZipCode />
        </CalculatorStepItem>
        <GasGuzzlerNewFuelEfficientVehicleStep
          price={paymentFields.vehicleSalesPrice}
          mpg={paymentFields.mpg}
          style={newStyle}
          onInputChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onTradeInPriceSave={saveVehicleSalesPrice}
          onVehicleUpdate={saveNewVehicle}
          onRemoveStyle={removeNewVehicle}
        />
        <GasGuzzlerTradeInStep
          mpg={paymentFields.tradeInMpg}
          price={paymentFields.tradeInPrice}
          style={tradeInStyle}
          onInputChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onTradeInPriceSave={saveTradeInPrice}
          onVehicleUpdate={saveTradeInVehicle}
          onRemoveStyle={removeTradeInVehicle}
          isTradeInVehicleSelectorOpen={isTradeInVehicleSelectorOpen}
        />
        <GasGuzzlerGasMileageStep
          mileagePerMonth={paymentFields.mileagePerMonth}
          fuelPerGallon={paymentFields.fuelPerGallon}
          style={tradeInStyle}
          onInputChange={handleInputChange}
          onKeyPress={handleKeyPress}
        />
        <GasGuzzlerResultsStep savings={savings} breakEvenPoint={breakEvenPoint} />
        <AboutCalculator title="About Gas Mileage Savings Calculator: Car Cost vs. Fuel Savings" items={ABOUT_ITEMS} />
      </CalculatorStepsWrapper>
    </Fragment>
  );
}

GasGuzzlerCalculator.propTypes = {
  isTradeInVehicleSelectorOpen: PropTypes.bool,
};
